import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { EntityDTO } from "~app/common/models/entity-models";
import { IExternOrganisation } from "~app/common/models/kontakter/KontaktDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { AtgardStatusesDTO } from "../dto/atgardstatuses/atgardstatuses";
import { VirkeskopDokumentInfoDTO } from "../dto/dokument/VirkeskopDokumentInfoDTO";
import { IVKKontraktLeverantor } from "../dto/kontrakt/kontrakt-betalningar-dto";
import { IVKKontraktKopplingarDTO } from "../dto/kontrakt/kontrakt-kopplingar-dto";
import { KontraktVacsStatusDTO } from "../dto/vacs/vacs-dto";
import { VKAtgardLeveransvirkesvolymDTO } from "../models/atgard-leveransvirkesvolym-models";
import { Atgard, IAtgardstyper, VKAtgardDTO } from "../models/atgard-models";
import { AtgardStatusExtraKartinfo, AtgardStatuses } from "../models/atgardstatus/atgardstatuses-models";
import { Avtal, VKAvtalResponseDTO } from "../models/avtal-models";
import { VKAvverkningsanmalanDTO } from "../models/avverkningsanmalan-models";
import { KontraktCertifikatDTO, VKCertifikatDTO } from "../models/certifiering-models";
import { VKGeometrierDTO } from "../models/karta-models";
import { Kontrakt, VKKontraktDTO } from "../models/kontrakt-models";
import { IFordelaLeveransplanQuery, IFordelaLeveransplanResponse, Leveransvirkessplan, VKLeveransvirkessplanDTO } from "../models/leveransvirkesplan-models";
import { AtgardNaturvardesbedomningDTO, NaturvardesomradeDTO } from "../models/naturvardesbedomning-models";
import { DirektutbyteBerakningRequest, DirektutbyteBerakningResponseDTO, VKDirektutbyteArborealDTO, VKDirektutbyteDTO } from "../models/tradslag/direktutbyte-models";
import { VKSnabbkalkylDTO, VKSnabbkalkylUtbyteDTO, VKSnabbkalkylUtbyteRequestDTO } from "../models/tradslag/snabbkalkyl-models";
import { VKForkalkylTradslagDTO } from "../models/tradslag/tradslag-models";
import {
    CreateRapportutskriftRequestDTO,
    CreateRapportutskriftResponseDTO,
    GetAttachedDokumentQuery,
    UtskriftTyp,
    VKKontraktUtskriftDTO
} from "../models/utskrift/utskrift-models";
import {
    CreateVirkeskopskontraktUtskriftRequestDTO as DefaultUtskriftValuesRequestDTO,
    GetVKAtgardExtraKartinfoQuery,
    VKUtskriftValuesDTO,
    VirkeskopskontraktUtskriftDTO
} from "../models/utskrift/utskriftValues-models";
import { VKKontraktNvbResponseDto } from "./../dto/kontrakt/kontrakt-nvb-dto";
import { VKSnabbkalkylmallDTO } from "./../models/tradslag/snabbkalkylmall-models";

@Injectable({
    providedIn: "root"
})
export class AvtalService {
    private readonly api = inject(ApiClientService);

    getAvtal(avtalid: number): Observable<Avtal> {
        return this.api.get<VKAvtalResponseDTO>(`virkeskop2/avtal/${avtalid}`).pipe(map(dto => new Avtal(dto)));
    }

    getAvtalByGuid(avtalGuid: string): Observable<Avtal> {
        return this.api.get<VKAvtalResponseDTO>(`virkeskop2/avtal/${avtalGuid}/guid`).pipe(map(dto => new Avtal(dto)));
    }

    getKontraktForAvtal(avtalid: number): Observable<EntityDTO[]> {
        return this.api.get<EntityDTO[]>(`virkeskop2/avtal/${avtalid}/kontrakt`);
    }

    getKontrakt(kontraktid: number): Observable<Kontrakt> {
        return this.api.get<VKKontraktDTO>(`virkeskop2/kontrakt/${kontraktid}`).pipe(map(dto => new Kontrakt(dto.guid, dto)));
    }

    getKontraktUtskriftValues(kontraktId: number) {
        return this.api.get<VKUtskriftValuesDTO>(`virkeskop2/kontrakt/${kontraktId}/utskrift`);
    }

    defaultUtskriftValues(kontraktId: number, options: DefaultUtskriftValuesRequestDTO) {
        return this.api.post<DefaultUtskriftValuesRequestDTO, VirkeskopskontraktUtskriftDTO>(
            `virkeskop2/kontrakt/${kontraktId}/defaultutskrift`, options);
    }

    getKontraktNvb(kontraktId: number) {
        return this.api.get<VKKontraktNvbResponseDto>(`virkeskop2/kontrakt/${kontraktId}/nvb`);
    }

    getKontraktBetalningar(kontraktId: number) {
        return this.api.get<IVKKontraktKopplingarDTO[]>(`virkeskop2/kontrakt/${kontraktId}/betalningar`);
    }

    getKontraktLeverantorer(kontraktId: number) {
        return this.api.get<IVKKontraktLeverantor[]>(`virkeskop2/kontrakt/${kontraktId}/leverantorer`);
    }

    getKontraktKopplingar(kontraktId: number) {
        return this.api.get<IVKKontraktKopplingarDTO>(`virkeskop2/kontrakt/${kontraktId}/kopplingar`);
    }

    getAtgarderForKontrakt(kontraktid: number): Observable<EntityDTO[]> {
        return this.api.get<EntityDTO[]>(`virkeskop2/kontrakt/${kontraktid}/atgarder`);
    }

    getGeometrierForKontrakt(kontraktid: number): Observable<VKGeometrierDTO> {
        return this.api.get<VKGeometrierDTO>(`virkeskop2/kontrakt/${kontraktid}/geometrier`);
    }

    getAtgard(atgardid: number): Observable<Atgard> {
        return this.api.get<VKAtgardDTO>(`virkeskop2/atgard/${atgardid}`).pipe(map(dto => new Atgard(dto)));
    }

    getLeveransvirkesvolymerForAtgard(kontraktId: number): Observable<VKAtgardLeveransvirkesvolymDTO[]> {
        return this.api.get<VKAtgardLeveransvirkesvolymDTO[]>(`virkeskop2/kontrakt/${kontraktId}/volymer`);
    }

    getGeometrierForAtgard(atgardid: number): Observable<EntityDTO[]> {
        return this.api.get<EntityDTO[]>(`virkeskop2/atgard/${atgardid}/geometrier`);
    }

    getAvverkningsanmalanForAtgard(atgardid: number): Observable<VKAvverkningsanmalanDTO> {
        return this.api.get<VKAvverkningsanmalanDTO>(`virkeskop2/atgard/${atgardid}/avverkningsanmalningar`);
    }

    getNaturvardesBedomning(atgardid: number): Observable<AtgardNaturvardesbedomningDTO> {
        return this.api.get<AtgardNaturvardesbedomningDTO>(`virkeskop2/atgard/${atgardid}/naturvardesbedomningar`);
    }

    getAtgardNvbOmrade(centrumkoordinat: string): Observable<NaturvardesomradeDTO> {
        return this.api.get<NaturvardesomradeDTO>(`virkeskopskontrakt/atgard/nvb`, false, { centrumkoordinat: centrumkoordinat });
    }

    getVirkeskopsatgardExtraKartInfo(atgardId: number, geometri: string): Observable<AtgardStatusExtraKartinfo> {
        return this.api.post<GetVKAtgardExtraKartinfoQuery, AtgardStatusExtraKartinfo>(`virkeskop2/atgard/extrakartinfo`, {
            atgardId: atgardId,
            geometri: geometri
        },
        false);
    }

    getCertifikat(): Observable<VKCertifikatDTO[]> {
        return this.api.get<VKCertifikatDTO[]>("virkeskop/certifikat");
    }

    getCertifikatForKontrakt(kontraktid: number): Observable<KontraktCertifikatDTO[]> {
        return this.api.get<KontraktCertifikatDTO[]>(`virkeskop2/kontrakt/${kontraktid}/certifikat`);
    }

    getAtgardstyper(kontraktstyp: string, affarsform: string) {
        return this.api.get<IAtgardstyper>(`virkeskopsatgardstyp?kontraktstyp=${encodeURIComponent(kontraktstyp)}&affarsform=${encodeURIComponent(affarsform)}`);
    }

    getFaltplanerareTypeAhead(input: string): Observable<ITypeAheadResult[]> {
        const subject = new Subject<ITypeAheadResult[]>();

        if (input.length < 3) {
            return subject.asObservable();
        }

        return this.api.post<any, ITypeAheadResult[]>("virkeskop/kopare/typeahead", JSON.stringify(input), false, null, ApiClientService.JsonHeader);
    }

    createKontraktUtskrifter(kontraktId: number, options: CreateRapportutskriftRequestDTO): Observable<CreateRapportutskriftResponseDTO> {
        return this.api.postRaw(
            `virkeskopskontrakt/${kontraktId}/rapportutskrift`,
            options,
            false,
            { timeout: 300000 }
        );
    }

    getDokumentInfo(query: GetAttachedDokumentQuery) {
        const kontraktId = query.parentId;
        const dokumentTyper = query.dokumentTyper || [];

        return this.api
            .get<VirkeskopDokumentInfoDTO[]>(`virkeskop2/${kontraktId}/dokument`)
            .pipe(
                map((dokument) =>
                    dokument
                        .map((d) => this.mapToUtskriftDTO(d))
                        .filter(
                            (d) =>
                                dokumentTyper.length === 0 || dokumentTyper.includes(d.typ)
                        )
                )
            );
    }

    updateVacs(kontraktId: number) {
        return this.api.post<string, KontraktVacsStatusDTO>(`virkeskop2/kontrakt/${kontraktId}/updatevacs`, null);
    }

    getVacsStatus(kontraktId: number) {
        return this.api.get<KontraktVacsStatusDTO>(`virkeskop2/kontrakt/${kontraktId}/vacsstatus`);
    }

    getLeveransvirkesplanForKontrakt(kontraktId: number): Observable<Leveransvirkessplan> {
        return this.api.get<VKLeveransvirkessplanDTO>(`virkeskop2/kontrakt/${kontraktId}/leveransvirkesplan`).pipe(map(dto => new Leveransvirkessplan(dto)));
    }

    getAllForkalkylTradslag(): Observable<VKForkalkylTradslagDTO[]> {
        return this.api.get<VKForkalkylTradslagDTO[]>("virkeskop2/forkalkyltradslag");
    }

    getSnabbkalkyl(atgardid: number): Observable<VKSnabbkalkylDTO> {
        return this.api.get<VKSnabbkalkylDTO>(`virkeskop2/atgard/${atgardid}/snabbkalkyl`);
    }

    getDirektutbyte(atgardid: number): Observable<VKDirektutbyteDTO> {
        return this.api.get<VKDirektutbyteDTO>(`virkeskop2/atgard/${atgardid}/direktutbyte`);
    }

    calculateSnabbkalkylUtbyte(request: VKSnabbkalkylUtbyteRequestDTO): Observable<VKSnabbkalkylUtbyteDTO> {
        return this.api.post<any, VKSnabbkalkylUtbyteDTO>(`virkeskop2/atgard/snabbkalkyl/calculate`,
            JSON.stringify(request), false, null, ApiClientService.JsonHeader);
    }

    calculateDirekUtbyte(request: DirektutbyteBerakningRequest): Observable<DirektutbyteBerakningResponseDTO> {
        return this.api.post<any, DirektutbyteBerakningResponseDTO>(`virkeskop2/atgard/direktutbyte/calculate`,
            JSON.stringify(request), false, null, ApiClientService.JsonHeader);
    }

    getLeverantorer(avtalId: number): Observable<ILeverantor[]> {
        return this.api.get<ILeverantor[]>(`virkeskop2/avtal/${avtalId}/virkesleverantorer`);
    }

    getLeverantorOrganisationer(leverantorId: number): Observable<IExternOrganisation[]> {
        return this.api.get<IExternOrganisation[]>(`virkeskop2/leverantor/${leverantorId}/leverantorsorganisationer`);
    }

    getAllSnabbKalkylmallar(): Observable<EntityDTO[]> {
        return this.api.get<EntityDTO[]>("virkeskop2/tradslag/snabbkalkylmall");
    }

    getSnabbKalkylmall(mallId: number): Observable<VKSnabbkalkylmallDTO> {
        return this.api.get<VKSnabbkalkylmallDTO>(`virkeskop2/tradslag/${mallId}/snabbkalkylmall`);
    }

    fordelaLeveransplan(request: IFordelaLeveransplanQuery): Observable<IFordelaLeveransplanResponse> {
        return this.api.post<IFordelaLeveransplanQuery, IFordelaLeveransplanResponse>(`virkeskopskontrakt/FordelaLeveransplan`, request);
    }

    getAtgardStatuses(atgardId: number): Observable<AtgardStatuses> {
        return this.api.get<AtgardStatusesDTO>(`virkeskop2/atgard/${atgardId}/statuses`, false)
            .pipe(map(dto => new AtgardStatuses(dto)));
    }

    getArboreal(atgardid: number): Observable<VKDirektutbyteArborealDTO> {
        return this.api.get<VKDirektutbyteArborealDTO>(`virkeskop2/atgard/${atgardid}/direktutbyte/arboreal`);

    }

    private mapToUtskriftDTO(dok: VirkeskopDokumentInfoDTO): VKKontraktUtskriftDTO {
        return {
            id: dok.id,
            version: dok.version,
            dokumentNamn: dok.filnamn,
            skapadTid: dok.skapadTidpunkt,
            typ: dok.dokumenttyp as UtskriftTyp,
            virkeskopskontraktSubEntityId: dok.virkeskopskontraktSubEntityId
        };
    }
}
